import {
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Typography,
  Spin,
} from "antd";
import isEqual from "lodash.isequal";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  clearFieldErrorMessageOnChange,
  isUserIdAvailableApi,
  onlyLatinCharsDigitsSpecialSymbolsRule,
  trimFormFieldValue,
  websiteRules,
} from "../../../utils/form.constants";
import { useNotificationOkCancel } from "../../../utils/hooks/useNotificationOkCancel.hook";
import { confirmModalUnsavedChangesProps } from "../../../pages/components/constants";
import { useCustomer } from "../../../utils/hooks/useCustomer.hook";
import { User } from "../../../interfaces/users";
import { useUpdateCustomer } from "../../../utils/hooks/useUpdateCustomer.hook";
import { buttonStyle } from "../../pages/auth/components/styles";
import { useTranslate } from "@refinedev/core";
import { ButtonListFooterModalSM } from "../../../pages/components/buttonsList";
import { addressRules, nameRules } from "./const/rules";
import { Rule } from "antd/es/form";
import { useIsAdminRole } from "../../../utils/hooks/useIdentityAdmin.hook";
import { SelectField } from "../../../components/inputs/select/select";
import { getWorldcheckVerificationStatusOptionLabel } from "../../../components/inputs/select/statusSelect.constants";
import { VerificationStatus } from "../../../utils/hooks/useWorldCheckVerification.hook";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { fetchFurtherInformation } from "../../../utils/fetchFurtherInformation";
import { companyRegistrationNumberRules } from "../../../pages/payment-request/addCompany.constants";
import { AutoComplete } from "../../../components/inputs/autocomplete";

interface IProps {
  formProps: FormProps;
  modalProps?: ModalProps;
  closeModal: () => void;
  editingId?: number;
  changeEditingId: (id: number | undefined) => void;
  refetchList: () => void;
  show: () => void;
}

export const EditCustomer = ({
  modalProps,
  formProps,
  editingId,
  changeEditingId,
  closeModal,
  refetchList,
  show,
}: IProps) => {
  const { data: loadData, isLoading: loadIsLoading } = useCustomer(editingId);
  const { update, isLoading: updateIsLoading } = useUpdateCustomer(editingId);
  const [loadedFormValues, setLoadedFormValues] = useState<User | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState<boolean>(false);
  const translate = useTranslate();
  const formIsLoading = loadIsLoading || updateIsLoading;
  const isAdminRole = useIsAdminRole();

  useEffect(() => {
    if (loadData) {
      formProps?.form?.setFieldsValue({
        fullName: loadData.fullName,
        email: loadData.email,
        smartUID: loadData.smartUID,
        address: loadData.address,
        website: loadData.website,
        worldCheckVerification: {
          status: loadData.worldCheckVerification.status,
          lastStatus: loadData.worldCheckVerification.lastStatus,
          regNo: loadData.regNo,
          companyCountry: loadData.country,
          directorName: loadData.fullName,
        },
      });
      setLoadedFormValues(formProps?.form?.getFieldsValue());
    } else {
      closeModal();
    }
  }, [loadData, formProps?.form, closeModal]);

  useEffect(() => {
    if (editingId !== undefined) {
      show();
    } else {
      closeModal();
    }
  }, [editingId, closeModal, show]);

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields();
    changeEditingId(undefined);
  }, [formProps.form, changeEditingId]);

  const onSubmit = useCallback(
    async (formValues: User) => {
      const isSuccess = await update({
        fullName: formValues.fullName,
        address: formValues.address,
        smartUID: formValues.smartUID,
        website: formValues.website,
        worldCheckVerification: {
          status: formValues.worldCheckVerification.status,
        },
      });

      if (isSuccess) {
        refetchList();
        onUnsavedChangesOk();
        closeModal();
      }
    },
    [refetchList, update, closeModal, onUnsavedChangesOk]
  );

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
  });

  const onModalCancel = useCallback(() => {
    const isFormChanged = formProps
      ? !isEqual(formProps.form?.getFieldsValue(), loadedFormValues)
      : false;
    if (isFormChanged) {
      openUnsavedChangesNotification();
    } else {
      onUnsavedChangesOk();
    }
  }, [
    formProps,
    loadedFormValues,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ]);

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps]);

  const userIdContainsSpaceValidator = useCallback(
    () => ({
      validator(_: any, value: string) {
        if (/\s/.test(value)) {
          return Promise.reject(new Error("SMART UID cannot contains spaces"));
        } else {
          return Promise.resolve();
        }
      },
      validateTrigger: "onSubmit",
    }),
    []
  );

  const userIdAvailableValidator = useCallback(
    () => ({
      async validator(_: any, value?: string) {
        if (
          value &&
          value.length >= 3 &&
          value.length <= 30 &&
          !/\s/.test(value) &&
          value !== loadedFormValues?.smartUID
        ) {
          const validationResult = await isUserIdAvailableApi(value);
          if (validationResult) {
            return Promise.resolve(true);
          } else {
            return Promise.reject("A user with this SMART UID already exists");
          }
        } else {
          return Promise.resolve(true);
        }
      },
      validateTrigger: "onSubmit",
    }),
    [loadedFormValues]
  );

  const emailRules: Rule[] = useMemo(
    () => [
      { required: true, validateTrigger: "onSubmit" },
      {
        type: "email",
        message: translate(
          "pages.register.errors.validEmail",
          "Invalid email address"
        ),
        validateTrigger: "onSubmit",
      },
    ],
    [translate]
  );

  async function handleFetchFurtherInformation() {
    await fetchFurtherInformation(
      loadData?.worldCheckVerification?.companyCaseId,
      loadData?.worldCheckVerification?.individualCaseId,
      setFetchingDetails
    );
  }

  const userIdRules: Rule[] = useMemo(
    () => [
      {
        required: true,
        message: "Please enter SMART UID",
        validateTrigger: "onSubmit",
        whitespace: true,
      },
      {
        min: 3,
        message: translate(
          "pages.register.errors.minUserId",
          "Minimum 3 characters"
        ),
        validateTrigger: "onSubmit",
      },
      {
        max: 30,
        message: translate(
          "pages.register.errors.maxUserId",
          "Maximum 30 characters"
        ),
        validateTrigger: "onSubmit",
      },
    ],
    [translate]
  );

  if (editingId === undefined) {
    return null;
  }

  return (
    <>
      <Modal
        {...modalProps}
        title={<p style={{ marginBottom: "0px" }}>Customer ID {editingId}</p>}
        className="smartModalTitle"
        width={""}
        onCancel={onModalCancel}
        footer={ButtonListFooterModalSM(onSave, onModalCancel, formIsLoading)}
      >
        {unsavedChangesContextHolder}
        <Form
          {...formProps}
          layout="vertical"
          onFinish={onSubmit}
          onValuesChange={clearFieldErrorMessageOnChange(formProps?.form)}
        >
          <Form.Item label="Name" name="fullName" rules={nameRules}>
            <Input placeholder="Name" style={buttonStyle} size="large" />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={emailRules}>
            <Input
              size="large"
              placeholder={translate("pages.register.fields.email", "Email")}
              style={buttonStyle}
              disabled
            />
          </Form.Item>
          <Form.Item
            label="Reg. No (TIN, VAT)"
            name={["worldCheckVerification", "regNo"]}
            rules={companyRegistrationNumberRules}
          >
            <Input
              placeholder="Reg. No (TIN, VAT)"
              style={{ ...buttonStyle }}
              size="large"
            />
          </Form.Item>
          <Form.Item
            label="Country"
            name={["worldCheckVerification", "companyCountry"]}
            rules={[
              {
                required: true,
                message: "Country of incorporation is required",
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoComplete
              resource="api/v1/worldCheck/countries"
              mapItem={(item: { value: string; label: string }) => ({
                value: item.label,
                key: item.value,
                label: <Typography.Text>{item.label}</Typography.Text>,
                data: item,
              })}
              notFoundPlaceholder="Start by typing country name"
            >
              <Input
                // style={{ fontSize: 16 }}
                placeholder="Country of incorporation"
                size="large"
                suffix={<DownOutlined />}
              />
            </AutoComplete>
          </Form.Item>
          <Form.Item
            label="Director's full name"
            name={["worldCheckVerification", "directorName"]}
            rules={[
              onlyLatinCharsDigitsSpecialSymbolsRule,
              {
                required: true,
                message: "Director's full name is required",
                validateTrigger: "onSubmit",
                whitespace: true,
              },
            ]}
          >
            <Input
              placeholder="Director's full name"
              style={{ ...buttonStyle }}
              size="large"
            />
          </Form.Item>
          <Form.Item
            label="SMART UID"
            name="smartUID"
            rules={[
              ...userIdRules,
              onlyLatinCharsDigitsSpecialSymbolsRule,
              userIdContainsSpaceValidator,
              userIdAvailableValidator,
            ]}
          >
            <Input
              disabled
              size="large"
              placeholder={translate(
                "pages.register.fields.userId",
                "SMART UID"
              )}
              style={buttonStyle}
            />
          </Form.Item>
          <Form.Item label="Address" name="address" rules={addressRules}>
            <Input placeholder="Address" style={buttonStyle} size="large" />
          </Form.Item>
          <Form.Item label="Website" name="website" rules={websiteRules}>
            <Input
              size="large"
              placeholder={translate(
                "pages.register.fields.website",
                "Website"
              )}
              onBlur={trimFormFieldValue("website", formProps?.form)}
              style={buttonStyle}
            />
          </Form.Item>
          {isAdminRole && (
            <>
              <Typography
                style={{ textAlign: "left", fontWeight: 500, fontSize: 16 }}
              >
                World Check verification status
              </Typography>
              <SelectField
                name={["worldCheckVerification", "status"]}
                getOptionLabel={getWorldcheckVerificationStatusOptionLabel}
                items={Object.values(VerificationStatus)}
              />
              {[
                VerificationStatus.HIGH_RISK_EDD,
                VerificationStatus.HIGH_RISK_FIR,
              ].includes(
                formProps?.form?.getFieldValue([
                  "worldCheckVerification",
                  "status",
                ])
              ) &&
                formProps?.form?.getFieldValue([
                  "worldCheckVerification",
                  "lastStatus",
                ]) === VerificationStatus.REQUIRED && (
                  <Typography style={{ textAlign: "left" }}>
                    <a onClick={handleFetchFurtherInformation}>See why</a>
                    {fetchingDetails && (
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{
                              fontSize: 16,
                              color: "blue",
                              marginLeft: "8px",
                            }}
                            spin
                          />
                        }
                      />
                    )}
                  </Typography>
                )}
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};
